/**
 * AssignmentRules — allgemeine Regeln für die Lead-Verteilung an Agenten (sevdesk-DNA).
 */
import { Button } from '@/components/ui/button';
import { BTN_SECONDARY, LABEL, SELECT } from '@/components/ausgaben/dna';
import { cn } from '@/lib/utils';

export const RULES = [
  { key: 'gebiet', label: 'Nach Gebiet & Auslastung', hint: 'Agent mit Kontakten in derselben PLZ/Region, sonst der mit den wenigsten offenen Aufgaben.' },
  { key: 'gleichmaessig', label: 'Gleichmäßig verteilen', hint: 'Immer an den Agenten mit den wenigsten offenen Aufgaben.' },
  { key: 'fest', label: 'Alle an einen Agenten', hint: 'Alle ausgewählten Leads an den gewählten Agenten.' },
];

export const agentLabel = (a) => a.name || [a.vorname, a.nachname].filter(Boolean).join(' ') || a.email;
export const openCount = (stats, email) => stats.get(String(email).toLowerCase())?.openTasks ?? 0;

export default function AssignmentRules({ agents, stats, rule, setRule, ruleAgents, setRuleAgents, fixedAgent, setFixedAgent, maxPerAgent, setMaxPerAgent, applyRule, canApply }) {
  const toggleAgent = (email) => {
    const next = new Set(ruleAgents);
    if (next.has(email)) next.delete(email); else next.add(email);
    setRuleAgents(next);
  };
  const current = RULES.find((r) => r.key === rule);

  return (
    <fieldset className="space-y-3">
      <legend className="mb-3 text-[15px] font-semibold text-dna-primary">Zuweisung</legend>
      <label className="block space-y-1.5">
        <span className={LABEL}>Regel</span>
        <select value={rule} onChange={(e) => setRule(e.target.value)} className={SELECT}>
          {RULES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
      </label>
      <p className="text-[13px] leading-snug text-dna-muted">{current?.hint}</p>

      {rule === 'fest' ? (
        <label className="block space-y-1.5">
          <span className={LABEL}>Agent</span>
          <select value={fixedAgent} onChange={(e) => setFixedAgent(e.target.value)} className={SELECT}>
            <option value="">Agent wählen</option>
            {agents.map((a) => <option key={a.email} value={a.email}>{agentLabel(a)}</option>)}
          </select>
        </label>
      ) : (
        <>
          <div className="space-y-1">
            <span className={LABEL}>Beteiligte Agenten</span>
            {agents.length === 0 && <p className="text-[13px] text-dna-muted">Keine aktiven Agenten gefunden.</p>}
            <ul className="divide-y divide-dna-line rounded-md border border-dna-line">
              {agents.map((a) => (
                <li key={a.email}>
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-2.5 px-3 text-[14px] text-dna-text hover:bg-dna-warm">
                    <input type="checkbox" className="h-4 w-4 accent-dna-primary" checked={ruleAgents.has(a.email)} onChange={() => toggleAgent(a.email)} />
                    <span className="min-w-0 flex-1 truncate">{agentLabel(a)}</span>
                    <span className="shrink-0 text-[12px] tabular-nums text-dna-muted">{openCount(stats, a.email)} offen</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
          <label className="flex items-center justify-between gap-3">
            <span className={LABEL}>Max. neue Leads je Agent</span>
            <input inputMode="numeric" value={maxPerAgent} placeholder="—" aria-label="Max. neue Leads je Agent"
              className="h-11 w-20 rounded-md border border-dna-field bg-white px-2.5 text-right text-[15px] tabular-nums text-dna-text placeholder:text-dna-placeholder focus:outline-none focus:ring-2 focus:ring-dna-focus"
              onChange={(e) => setMaxPerAgent(e.target.value.replace(/[^\d]/g, '').slice(0, 3))} />
          </label>
        </>
      )}
      <Button type="button" variant="ghost" className={cn(BTN_SECONDARY, 'w-full')} disabled={!canApply} onClick={applyRule}>
        Regel auf Auswahl anwenden
      </Button>
    </fieldset>
  );
}
